import { cx } from '@emotion/css';
import { parseDate } from '@internationalized/date';
import type { DateFieldProps } from '@zinc/ui';
import { DateField, FormControl, View } from '@zinc/ui';
import useConst from 'bz-utilities/hooks/const';
import useUpdateEffect from 'bz-utilities/hooks/update-effect';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import type { DateValue } from 'react-aria-components';
import { I18nProvider } from 'react-aria-components';
import { cache } from 'tm3-core/cache';

type RDateFieldProps = Required<DateFieldProps>;

export interface IProps {
  autoOk?: boolean;
  className?: string;
  /** Commit complete DateField values immediately instead of waiting for blur. */
  commitOnChange?: boolean;
  clearable?: boolean;
  dataTestId?: string;
  disabled?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  errorText?: string;
  label: string;
  max?: string | moment.Moment | null;
  min?: string | moment.Moment | null;
  readOnly?: boolean;
  showTodayButton?: boolean;
  submitted?: boolean;
  value: string | moment.MomentInput | null;
  onChange: (value: moment.Moment | null) => void;
  removeLegacyPadding?: boolean;
  /** Forwarded to Zinc DateField/DatePicker root (e.g. defaultFocusedValue). */
  attributes?: Record<string, unknown>;
}
const FieldDatePicker = React.forwardRef((props: IProps, ref) => {
  return <WrappedDateField ref={ref} {...props} />;
});

FieldDatePicker.displayName = 'FieldDatePicker';
export default FieldDatePicker;

const toDateValue = (input: string | moment.Moment | null | undefined): DateValue | undefined => {
  if (!input) return undefined;

  const m = moment.isMoment(input) ? input : moment(input);
  if (!m.isValid()) return undefined;

  return parseDate(m.format('YYYY-MM-DD'));
};

const WrappedDateField = React.forwardRef((props: IProps, ref) => {
  const {
    value,
    min,
    max,
    commitOnChange = false,
    errorText,
    submitted,
    dLabel,
    label,
    className,
    onChange,
    dataTestId,
    removeLegacyPadding = false,
    ...other
  } = props;
  const placeholderValue = useConst(() => parseDate(moment().format('YYYY-MM-DD')));

  const computedLabel = useMemo(() => {
    return dLabel ? (cache.dynamicLabels.GetLabel(dLabel)?.text ?? label ?? '') : label;
  }, [label, dLabel]);

  const dateValue = useMemo(() => {
    if (!value) return undefined;
    const m = moment(value);
    if (!m.isValid()) return undefined;
    return parseDate(m.format('YYYY-MM-DD'));
  }, [value]);

  const [internalValue, setInternalValue] = useState<DateValue | undefined>(dateValue);

  useUpdateEffect(() => {
    setInternalValue(dateValue);
  }, [dateValue]);

  const minDate = useMemo(() => {
    const parsedMin = toDateValue(min);
    const fallback = parseDate('1900-01-01');
    if (!parsedMin || parsedMin.year < fallback.year) {
      return fallback;
    }
    return parsedMin;
  }, [min]);

  const maxDate = useMemo(() => {
    return toDateValue(max);
  }, [max]);

  const _className = useMemo(() => {
    return cx(className, 'border', {
      '!border-orange-300': !!errorText && !submitted,
      '!border-red-300': !!errorText && submitted
    });
  }, [className, errorText, submitted]);

  const onChangeDate: RDateFieldProps['onChange'] = useCallback(
    (e) => {
      setInternalValue(e);
      if (!commitOnChange || !e || e.year < minDate.year) return;

      const iso = e.toDate('UTC');
      if (iso) {
        onChange(moment.utc(iso, 'YYYY-MM-DD'));
      }
    },
    [commitOnChange, minDate, onChange]
  );

  const onBlur = useCallback(() => {
    if (commitOnChange) return;
    if (!internalValue) return;

    const blurDate = internalValue.year >= minDate.year ? internalValue : minDate;
    const iso = blurDate.toDate('UTC');
    if (!iso) return;

    onChange(moment.utc(iso, 'YYYY-MM-DD'));
  }, [commitOnChange, onChange, minDate, internalValue]);

  const onPaste = useCallback(
    (e) => {
      const input = e.clipboardData.getData('Text'); //'03/21/1988';
      const parsedMoment = moment(input, moment.localeData().longDateFormat('L'), true); // strict mode
      if (parsedMoment.isValid()) onChange(parsedMoment);
    },
    [onChange]
  );
  return (
    <View
      paddingBottom={removeLegacyPadding ? 0 : 2}
      attributes={{ onPaste }}
      className="max-w-200px"
    >
      <FormControl hasError={submitted && !!errorText}>
        {computedLabel && <FormControl.Label>{computedLabel}</FormControl.Label>}
        <I18nProvider locale={window.localStorage.locale}>
          <DateField
            ref={ref}
            {...other}
            minValue={minDate}
            maxValue={maxDate}
            value={internalValue}
            onChange={onChangeDate}
            onBlur={onBlur}
            className={_className}
            attributes={{
              'data-testid': dataTestId,
              placeholderValue: placeholderValue
            }}
          />
        </I18nProvider>
        <FormControl.Error>
          <View className="max-w-[150px]">{errorText}</View>
        </FormControl.Error>
      </FormControl>
    </View>
  );
});
